// Module: webauthn
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §2.2, §3.3, §5.2, §8.1, §8.2
//
// The designated platform adapter: the one module permitted to depend on browser-only globals,
// and therefore the one a Tauri or Node port replaces (spec §9.3).

export type AuthenticatorKind = "roaming" | "platform"

export type CreatedCredential = {
  credentialId: Uint8Array
  /** DER SubjectPublicKeyInfo, as returned by getPublicKey() — not COSE. `worker` imports it
   *  directly with crypto.subtle.importKey("spki", ...). */
  publicKey: Uint8Array
  signCount: number
  transports: string[]
  prfSupported: boolean
}

export type Assertion = {
  credentialId: Uint8Array
  authenticatorData: Uint8Array
  clientDataJSON: Uint8Array
  signature: Uint8Array
  userHandle: Uint8Array | null
}

export type PrfOutputs = { first: Uint8Array; second?: Uint8Array }

export type Capabilities = {
  webauthn: boolean
  platformAuthenticator: boolean
  prf: "supported" | "unsupported" | "unknown"
}

export type WebAuthnErrorCode =
  | "OriginMismatch"
  | "NotSupported"
  | "UserCancelled"
  | "Timeout"
  | "NoCredential"
  | "PrfUnavailable"
  | "CredentialExists"
  | "AuthenticatorError"

export class WebAuthnError extends Error {
  readonly code: WebAuthnErrorCode
  constructor(code: WebAuthnErrorCode, message?: string, options?: { cause?: unknown }) {
    super(message ?? code, options)
    this.name = "WebAuthnError"
    this.code = code
  }
}

export type WebAuthnConfig = {
  rpId: string
  rpName: string
  /** The only origin this build may run against. Pinned at build time (spec §8.1). */
  origin: string
  /** Injected seam. Defaults to navigator.credentials. */
  credentials?: CredentialsContainer
  /** Injected seam. Defaults to globalThis.location?.origin. */
  currentOrigin?: string
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 120_000

const bytes = (source: ArrayBuffer | ArrayBufferView): Uint8Array =>
  source instanceof ArrayBuffer
    ? new Uint8Array(source)
    : new Uint8Array(source.buffer, source.byteOffset, source.byteLength)

/** Maps a raw ceremony rejection onto the contract's error union. */
function mapError(cause: unknown, phase: "create" | "get"): WebAuthnError {
  const name = (cause as { name?: string } | null)?.name
  switch (name) {
    case "AbortError":
    case "TimeoutError":
      return new WebAuthnError("Timeout", "the ceremony timed out", { cause })
    case "NotAllowedError":
      return new WebAuthnError("UserCancelled", "the ceremony was dismissed", { cause })
    case "InvalidStateError":
      return phase === "create"
        ? new WebAuthnError("CredentialExists", "this authenticator is already enrolled", { cause })
        : new WebAuthnError("AuthenticatorError", "authenticator is in an invalid state", { cause })
    case "NotSupportedError":
      return new WebAuthnError("NotSupported", "the requested options are not supported", { cause })
    case "SecurityError":
      return new WebAuthnError("OriginMismatch", "the origin is not valid for this RP ID", { cause })
    default:
      return new WebAuthnError("AuthenticatorError", `ceremony failed: ${String(name ?? cause)}`, {
        cause,
      })
  }
}

export interface WebAuthn {
  capabilities(): Promise<Capabilities>
  createCredential(opts: {
    kind: AuthenticatorKind
    userId: Uint8Array
    userName: string
    userDisplayName: string
    excludeCredentialIds?: Uint8Array[]
  }): Promise<CreatedCredential>
  assert(opts: {
    challenge: Uint8Array
    allowCredentialIds?: Uint8Array[]
    prfInputs?: { first: Uint8Array; second?: Uint8Array }
  }): Promise<{ assertion: Assertion; prf?: PrfOutputs }>
}

/**
 * Construction fails if the document origin is not the pinned one.
 *
 * This is the enforcement point for spec §8.1: PRF output is bound to the RP ID, so running
 * against a preview URL would silently derive keys that can never open the production vault, and
 * moving origins after enrolment destroys access permanently. Failing loudly here beats deriving
 * a wrong key quietly. If the origin cannot be determined at all, construction still fails —
 * unverifiable is treated as wrong.
 */
export function createWebAuthn(config: WebAuthnConfig): WebAuthn {
  let configured: URL
  try {
    configured = new URL(config.origin)
  } catch (cause) {
    throw new WebAuthnError("OriginMismatch", `origin is not a URL: ${config.origin}`, { cause })
  }

  // WebAuthn requires a secure context. localhost is the documented exception, and dev vaults are
  // permanently distinct from production ones anyway (spec §8.1).
  if (configured.protocol !== "https:" && configured.hostname !== "localhost") {
    throw new WebAuthnError("OriginMismatch", `origin must be https: ${config.origin}`)
  }

  // The RP ID must be the FULL host, never a registrable suffix of it.
  //
  // WebAuthn would permit a page at vault.example.com to claim rpId "example.com". Doing so would
  // let every sibling subdomain — a game, a demo, anything ever deployed under the parent — request
  // PRF assertions against vault credentials and derive the hardware factor. The mistake is silent
  // and irreversible once a credential exists, so it is refused here rather than trusted not to
  // happen.
  if (config.rpId !== configured.hostname) {
    throw new WebAuthnError(
      "OriginMismatch",
      `rpId must equal the origin host: rpId is "${config.rpId}", origin host is ` +
        `"${configured.hostname}". A registrable suffix would expose these credentials to every ` +
        `sibling subdomain.`,
    )
  }

  const currentOrigin =
    config.currentOrigin ?? (globalThis as { location?: { origin?: string } }).location?.origin

  if (currentOrigin === undefined) {
    throw new WebAuthnError("OriginMismatch", "cannot determine the current origin")
  }
  if (currentOrigin !== config.origin) {
    throw new WebAuthnError(
      "OriginMismatch",
      `running at ${currentOrigin}, but this build is pinned to ${config.origin}`,
    )
  }

  const timeout = config.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const container = (): CredentialsContainer => {
    const resolved =
      config.credentials ??
      (globalThis as { navigator?: { credentials?: CredentialsContainer } }).navigator?.credentials
    if (!resolved) throw new WebAuthnError("NotSupported", "WebAuthn is unavailable")
    return resolved
  }

  return {
    async capabilities(): Promise<Capabilities> {
      const PK = (globalThis as { PublicKeyCredential?: typeof PublicKeyCredential })
        .PublicKeyCredential
      if (!PK) return { webauthn: false, platformAuthenticator: false, prf: "unsupported" }

      let platformAuthenticator = false
      try {
        platformAuthenticator = await PK.isUserVerifyingPlatformAuthenticatorAvailable()
      } catch {
        platformAuthenticator = false
      }

      // PRF support cannot be probed without running a ceremony, which needs a user gesture.
      // Callers must treat "unknown" as "attempt and handle failure", never as "supported".
      return { webauthn: true, platformAuthenticator, prf: "unknown" }
    },

    async createCredential(opts): Promise<CreatedCredential> {
      // residentKey and userVerification are fixed, not parameters: spec §2.2 requires a
      // discoverable credential and §8.2 requires user verification. Letting a caller vary them
      // would allow creating a credential that cannot open its own vault.
      const publicKey: PublicKeyCredentialCreationOptions = {
        rp: { id: config.rpId, name: config.rpName },
        user: {
          id: opts.userId as BufferSource,
          name: opts.userName,
          displayName: opts.userDisplayName,
        },
        challenge: crypto.getRandomValues(new Uint8Array(32)) as BufferSource,
        // ES256 only. `worker` verifies assertions itself with no crypto library, and an
        // RS256 path it could never exercise against a real key would be worse than no path.
        // YubiKeys and current platform authenticators all support ES256.
        pubKeyCredParams: [{ type: "public-key", alg: -7 }],
        authenticatorSelection: {
          authenticatorAttachment: opts.kind === "platform" ? "platform" : "cross-platform",
          residentKey: "required",
          requireResidentKey: true,
          userVerification: "required",
        },
        excludeCredentials: (opts.excludeCredentialIds ?? []).map((id) => ({
          type: "public-key" as const,
          id: id as BufferSource,
        })),
        extensions: { prf: {} } as AuthenticationExtensionsClientInputs,
        timeout,
      }

      let credential: Credential | null
      try {
        credential = await container().create({ publicKey })
      } catch (cause) {
        throw mapError(cause, "create")
      }
      if (!credential) throw new WebAuthnError("NoCredential", "no credential was created")

      const pk = credential as PublicKeyCredential
      const response = pk.response as AuthenticatorAttestationResponse
      const extensions = pk.getClientExtensionResults() as { prf?: { enabled?: boolean } }

      const publicKeyBytes = response.getPublicKey?.()
      if (!publicKeyBytes) {
        throw new WebAuthnError("AuthenticatorError", "authenticator returned no public key")
      }

      return {
        credentialId: bytes(pk.rawId),
        publicKey: bytes(publicKeyBytes),
        signCount: response.getAuthenticatorData
          ? new DataView(response.getAuthenticatorData()).getUint32(33, false)
          : 0,
        transports: response.getTransports?.() ?? [],
        prfSupported: extensions.prf?.enabled === true,
      }
    },

    async assert(opts): Promise<{ assertion: Assertion; prf?: PrfOutputs }> {
      const allow = opts.allowCredentialIds ?? []
      const publicKey: PublicKeyCredentialRequestOptions = {
        rpId: config.rpId,
        challenge: opts.challenge as BufferSource,
        userVerification: "required",
        // An empty allowlist makes the browser resolve a discoverable credential — the mechanism
        // behind "a fresh laptop needs only the key" (spec §7.2).
        allowCredentials: allow.map((id) => ({ type: "public-key" as const, id: id as BufferSource })),
        timeout,
        ...(opts.prfInputs
          ? {
              extensions: {
                prf: {
                  eval: {
                    first: opts.prfInputs.first as BufferSource,
                    ...(opts.prfInputs.second ? { second: opts.prfInputs.second as BufferSource } : {}),
                  },
                },
              } as AuthenticationExtensionsClientInputs,
            }
          : {}),
      }

      let credential: Credential | null
      try {
        credential = await container().get({ publicKey })
      } catch (cause) {
        throw mapError(cause, "get")
      }
      if (!credential) throw new WebAuthnError("NoCredential", "no credential matched")

      const pk = credential as PublicKeyCredential
      const response = pk.response as AuthenticatorAssertionResponse

      const assertion: Assertion = {
        credentialId: bytes(pk.rawId),
        authenticatorData: bytes(response.authenticatorData),
        clientDataJSON: bytes(response.clientDataJSON),
        signature: bytes(response.signature),
        userHandle: response.userHandle ? bytes(response.userHandle) : null,
      }

      if (!opts.prfInputs) return { assertion }

      const results = (
        pk.getClientExtensionResults() as {
          prf?: { results?: { first?: ArrayBuffer; second?: ArrayBuffer } }
        }
      ).prf?.results

      // Fail rather than return a partial success: a caller that proceeded without the PRF output
      // would derive a wrong key and present the vault as unopenable.
      if (!results?.first) {
        throw new WebAuthnError("PrfUnavailable", "the authenticator returned no PRF result")
      }
      if (opts.prfInputs.second && !results.second) {
        throw new WebAuthnError("PrfUnavailable", "the authenticator returned only one PRF result")
      }

      return {
        assertion,
        prf: {
          first: bytes(results.first),
          ...(results.second ? { second: bytes(results.second) } : {}),
        },
      }
    },
  }
}
